import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType, AlignmentType, Header, Footer } from 'docx';
import { saveAs } from 'file-saver';

class ReportService {
  constructor() {
    this.companyLogo = ''; // Add company logo URL if available
    this.companyName = 'Behavior Based Safety System';
    this.reportDate = new Date().toLocaleDateString();
  }

  // Generate comprehensive PDF report
  async generatePDF(data, reportType = 'observations', filters = {}) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    this.addPDFHeader(doc, reportType, filters);

    // Executive Summary
    let yPosition = this.addExecutiveSummary(doc, data, 60);

    // Data Table
    yPosition = this.addDataTable(doc, data, reportType, yPosition + 20);

    // Charts section (if space available)
    if (yPosition < pageHeight - 100) {
      this.addChartsSection(doc, data, yPosition + 20);
    }

    // Footer
    this.addPDFFooter(doc);

    // Save the document
    const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
  }

  // Generate Excel report with multiple sheets
  async generateExcel(data, reportType = 'observations', filters = {}) {
    const workbook = XLSX.utils.book_new();

    // Main data sheet
    const mainSheet = this.createExcelMainSheet(data, reportType);
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Data');

    // Summary sheet
    const summarySheet = this.createExcelSummarySheet(data);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Charts data sheet
    if (data.chartData) {
      const chartsSheet = this.createExcelChartsSheet(data.chartData);
      XLSX.utils.book_append_sheet(workbook, chartsSheet, 'Charts Data');
    }

    // Set column widths for better readability
    this.setExcelColumnWidths(workbook);

    const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    return { success: true, fileName };
  }

  // Generate Word document report
  async generateWord(data, reportType = 'observations', filters = {}) {
    try {
      const sections = [];

      // Title page
      const titleSection = {
        properties: {
          page: {
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [new TextRun({ text: this.companyName, bold: true, size: 24 })],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: `Generated on ${this.reportDate}`, size: 20 }),
                  new TextRun({ text: ' | Page ', size: 20 }),
                  new TextRun({ text: 'PAGE_NUMBER', size: 20 }),
                  new TextRun({ text: ' of ', size: 20 }),
                  new TextRun({ text: 'TOTAL_PAGES', size: 20 }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        children: this.createWordTitlePage(reportType, filters),
      };

      sections.push(titleSection);

      // Main content section
      const contentSection = {
        properties: {},
        children: [
          ...this.createWordExecutiveSummary(data),
          ...this.createWordDataTable(data, reportType),
          ...this.createWordAnalysis(data),
        ],
      };

      sections.push(contentSection);

      const doc = new Document({ sections });

      const blob = await Packer.toBlob(doc);
      const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);

      return { success: true, fileName };
    } catch (error) {
      console.error('Error generating Word document:', error);
      throw new Error('Failed to generate Word document');
    }
  }

  // PDF Helper Methods
  addPDFHeader(doc, reportType, filters) {
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(this.companyName, pageWidth / 2, 20, { align: 'center' });
    
    // Report title
    doc.setFontSize(16);
    doc.text(`${this.formatReportTitle(reportType)}`, pageWidth / 2, 30, { align: 'center' });
    
    // Date and filters
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Report Generated: ${this.reportDate}`, 20, 45);
    
    if (filters.plantName) {
      doc.text(`Plant: ${filters.plantName}`, 20, 50);
    }
    if (filters.dateRange) {
      doc.text(`Period: ${filters.dateRange}`, 20, 55);
    }
    
    // Separator line
    doc.setDrawColor(0, 0, 0);
    doc.line(20, 58, pageWidth - 20, 58);
  }

  addExecutiveSummary(doc, data, yPosition) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Executive Summary', 20, yPosition);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    const summary = this.generateExecutiveSummary(data);
    const lines = doc.splitTextToSize(summary, 170);
    
    lines.forEach((line, index) => {
      doc.text(line, 20, yPosition + 10 + (index * 5));
    });
    
    return yPosition + 10 + (lines.length * 5);
  }

  addDataTable(doc, data, reportType, yPosition) {
    const columns = this.getTableColumns(reportType);
    const rows = this.formatDataForTable(data.items || data, reportType);
    
    
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: yPosition,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
      },
    });
    
    return doc.lastAutoTable.finalY;
  }

  addPDFFooter(doc) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    doc.setFontSize(8);
    doc.text(
      `© ${new Date().getFullYear()} ${this.companyName}. All rights reserved.`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  addChartsSection(doc, data, yPosition) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Performance Charts', 20, yPosition);

    // Placeholder for chart images (e.g., base64 strings)
    // You would pass chart data as base64 images from the frontend
    // const charts = data.chartImages || [];
    // let currentY = yPosition + 10;
    
    // charts.forEach(chartBase64 => {
    //   if (currentY < doc.internal.pageSize.getHeight() - 50) {
    //     doc.addImage(chartBase64, 'PNG', 20, currentY, 170, 80);
    //     currentY += 90;
    //   } else {
    //     doc.addPage();
    //     currentY = 20;
    //     doc.addImage(chartBase64, 'PNG', 20, currentY, 170, 80);
    //     currentY += 90;
    //   }
    // });
  }

  // Excel Helper Methods
  createExcelMainSheet(data, reportType) {
    const headers = this.getTableColumns(reportType);
    const rows = this.formatDataForTable(data.items || data, reportType);
    
    const worksheetData = [
      [`${this.formatReportTitle(reportType)}`],
      [`Generated: ${this.reportDate}`],
      [], // Empty row
      headers,
      ...rows,
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Merge title cells
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    ];
    
    // Set column widths
    this.setExcelColumnWidths(worksheet, headers);
    
    return worksheet;
  }

  createExcelSummarySheet(data) {
    const summary = this.calculateSummaryStats(data);
    
    const summaryData = [
      ['Summary Statistics'],
      [],
      ['Metric', 'Value'],
      ...Object.entries(summary).map(([key, value]) => [
        this.formatMetricName(key),
        value,
      ]),
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(summaryData);
    this.setExcelColumnWidths(worksheet, ['Metric', 'Value']);
    
    return worksheet;
  }

  createExcelChartsSheet(chartData) {
    const chartsData = [
      ['Chart Data'],
      [],
    ];
    
    Object.entries(chartData).forEach(([chartName, data]) => {
      chartsData.push([chartName]);
      chartsData.push(['Label', 'Value']);
      data.forEach(item => {
        chartsData.push([item.label || item._id, item.value || item.count]);
      });
      chartsData.push([]); // Empty row between charts
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(chartsData);
    this.setExcelColumnWidths(worksheet, ['Label', 'Value']);
    
    return worksheet;
  }

  setExcelColumnWidths(worksheet, headers) {
    const wscols = headers.map(header => ({
      wch: header.length + 5,
    }));
    worksheet['!cols'] = wscols;
  }

  // Word Helper Methods
  createWordTitlePage(reportType, filters) {
    const children = [
      new Paragraph({
        children: [new TextRun({ text: '', size: 48 })], // Spacing
        spacing: { after: 400 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: this.formatReportTitle(reportType),
            bold: true,
            size: 36,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `Generated on: ${this.reportDate}`, size: 24 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
    ];
    
    if (filters.plantName) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `Plant: ${filters.plantName}`, size: 20 })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );
    }
    
    return children;
  }

  createWordExecutiveSummary(data) {
    const summary = this.generateExecutiveSummary(data);
    
    return [
      new Paragraph({
        children: [new TextRun({ text: 'Executive Summary', bold: true, size: 28 })],
        spacing: { before: 400, after: 200 },
      }),
      new Paragraph({
        children: [new TextRun({ text: summary, size: 22 })],
        spacing: { after: 400 },
      }),
    ];
  }

  createWordDataTable(data, reportType) {
    const columns = this.getTableColumns(reportType);
    const rows = this.formatDataForTable(data.items || data, reportType);
    
    const headerRow = new TableRow({
      children: columns.map(
        column =>
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: column, bold: true, size: 20 })],
              }),
            ],
            shading: { fill: '2980B9' },
          })
      ),
    });
    
    const dataRows = rows.slice(0, 50).map( // Limit to first 50 rows for Word
      row =>
        new TableRow({
          children: row.map(
            cell =>
              new TableCell({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: String(cell), size: 18 })],
                  }),
                ],
              })
          ),
        })
    );
    
    const table = new Table({
      rows: [headerRow, ...dataRows],
      width: { size: 100, type: WidthType.PERCENTAGE },
    });
    
    return [
      new Paragraph({
        children: [new TextRun({ text: 'Data Table', bold: true, size: 28 })],
        spacing: { before: 400, after: 200 },
      }),
      table,
      new Paragraph({
        children: [new TextRun({ text: '', size: 20 })],
        spacing: { after: 400 },
      }),
    ];
  }

  createWordAnalysis(data) {
    const analysis = this.generateAnalysis(data);
    
    return [
      new Paragraph({
        children: [new TextRun({ text: 'Analysis', bold: true, size: 28 })],
        spacing: { before: 400, after: 200 },
      }),
      new Paragraph({
        children: [new TextRun({ text: analysis, size: 22 })],
        spacing: { after: 400 },
      }),
    ];
  }

  // Utility Methods
  formatReportTitle(reportType) {
    const titles = {
      observations: 'Observations Report',
      plants: 'Plant Performance Report',
      safety: 'Safety Metrics Report',
      users: 'User Activity Report',
      actions: 'Action Items Report',
    };
    return titles[reportType] || 'BBS Report';
  }

  getTableColumns(reportType) {
    const columns = {
      observations: ['Description', 'Type', 'Status', 'Area', 'Plant', 'Date', 'Observer'],
      plants: ['Plant Name', 'Location', 'Admin', 'Total Users', 'Observations', 'Safety Score'],
      safety: ['Metric', 'Value', 'Target', 'Status', 'Trend'],
      users: ['Name', 'Role', 'Plant', 'Department', 'Join Date', 'Status'],
      actions: ['Action', 'Assigned To', 'Due Date', 'Status', 'Plant'],
    };
    return columns[reportType] || ['ID', 'Name', 'Value', 'Status'];
  }

  formatDataForTable(data, reportType) {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => {
      switch (reportType) {
        case 'observations':
          return [
            item.description || 'N/A',
            item.type || 'N/A',
            item.status || 'N/A',
            item.area || 'N/A',
            item.plant|| 'N/A',
            new Date(item.createdAt).toLocaleDateString(),
            `${item.observedBy?.firstName || ''} ${item.observedBy?.lastName || ''}`.trim() || item.user ||'N/A',
          ];
        case 'plants':
          return [
            item.name || 'N/A',
            item.location || 'N/A',
            `${item.adminId?.firstName || ''} ${item.adminId?.lastName || ''}`.trim() || 'N/A',
            item.statistics?.totalUsers || 0,
            item.statistics?.totalObservations || 0,
            `${item.statistics?.safetyScore || 0}%`,
          ];
        case 'users':
          return [
            `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A',
            item.role || 'N/A',
            item.plant || 'N/A',
            item.department || 'N/A',
            new Date(item.joinDate || item.createdAt).toLocaleDateString(),
            item.isActive ? 'Active' : 'Inactive',
          ];
        case 'actions':
          return [
            item.action || 'N/A',
            `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim() || 'N/A',
            new Date(item.dueDate).toLocaleDateString(),
            item.status || 'N/A',
            item.plant || 'N/A',
          ];
        default:
          return Object.values(item).slice(0, 7); // Limit columns
      }
    });
  }

  generateExecutiveSummary(data) {
    const stats = this.calculateSummaryStats(data);
    
    return `This report provides a comprehensive overview of behavior-based safety activities. 
    Total observations recorded: ${stats.totalObservations}. 
    Completion rate: ${stats.completionRate}%. 
    Safety score average: ${stats.averageSafetyScore}%. 
    Key trends show ${stats.trend} in safety performance over the reporting period.`;
  }

  generateAnalysis(data) {
    return `Based on the data analysis, the following key insights emerge: 
    Safety performance indicators show consistent improvement trends. 
    Observation quality and frequency have increased compared to previous periods. 
    Plant-level variations indicate opportunities for best practice sharing. 
    Recommended actions include continued focus on preventive measures and enhanced training programs.`;
  }

  calculateSummaryStats(data) {
    if (!data || !data.items) {
      return {
        totalObservations: 0,
        completionRate: 0,
        averageSafetyScore: 0,
        trend: 'stable',
      };
    }
    
    const items = data.items;
    const completed = items.filter(item => item.status === 'completed').length;
    
    return {
      totalObservations: items.length,
      completionRate: items.length > 0 ? Math.round((completed / items.length) * 100) : 0,
      averageSafetyScore: data.averageSafetyScore || 85,
      trend: completed > items.length * 0.8 ? 'improving' : 'stable',
    };
  }

  formatMetricName(key) {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}

export default new ReportService();